import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '@/utils/dateFormatter';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Helper function to format date ranges
const formatDateRange = (dateString: string): string => {
  if (!dateString || dateString.trim() === '') {
    return 'Not specified';
  }
  
  // Handle various date range formats
  const dateRangePatterns = [
    // YYYY-MM - YYYY-MM format
    /^(\d{4}-\d{2})\s*-\s*(\d{4}-\d{2})$/,
    // MM/YYYY - MM/YYYY format  
    /^(\d{1,2}\/\d{4})\s*-\s*(\d{1,2}\/\d{4})$/,
    // YYYY-MM-DD - YYYY-MM-DD format
    /^(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})$/,
    // MM/DD/YYYY - MM/DD/YYYY format
    /^(\d{1,2}\/\d{1,2}\/\d{4})\s*-\s*(\d{1,2}\/\d{1,2}\/\d{4})$/
  ];
  
  for (const pattern of dateRangePatterns) {
    const match = dateString.match(pattern);
    if (match) {
      const startDate = match[1];
      const endDate = match[2];
      
      try {
        // Try to format each date individually
        const formattedStart = formatDate(startDate);
        const formattedEnd = formatDate(endDate);
        
        if (formattedStart !== 'Invalid date' && formattedEnd !== 'Invalid date') {
          return `${formattedStart} - ${formattedEnd}`;
        }
      } catch (error) {
        console.warn('Error formatting date range:', error);
      }
    }
  }
  
  // If no pattern matches, try to format as a single date
  try {
    const formatted = formatDate(dateString);
    if (formatted !== 'Invalid date') {
      return formatted;
    }
  } catch (error) {
    console.warn('Error formatting single date:', error);
  }
  
  // If all else fails, return the original string
  return dateString;
};
import { 
  Briefcase, 
  ChevronDown, 
  ChevronUp, 
  Users, 
  Calendar,
  User,
  ExternalLink,
  Info,
  Clock,
  AlertTriangle,
  TrendingUp,
  Building2,
  Search
} from 'lucide-react';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '@/components/ui/accordion';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ViewToggle, ViewMode } from '@/components/common/ViewToggle';
import { useTranslation } from 'react-i18next';
import { CompanyPosition, PositionCandidate } from '@/hooks/companies/useCompanyPositions';
import { SeniorityPills } from '@/components/roles/SeniorityPills';

interface CompanyPositionsTabProps {
  companyId: string;
  companyName: string;
  positions: CompanyPosition[];
  loading: boolean;
  error: string | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

interface CandidatePopoverProps {
  candidate: PositionCandidate;
  positionTitle: string;
}

const CandidatePopover = ({ candidate, positionTitle }: CandidatePopoverProps) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Briefcase className="h-4 w-4 text-blue-500" />
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">{positionTitle} Role</h4>
      </div>

      {/* Candidate Info Card */}
      <div className="bg-blue-50 dark:bg-blue-950 rounded-sm p-4 border border-blue-200 dark:border-blue-800">
        <div className="space-y-3">
          {/* Candidate Name */}
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-blue-500" />
            <span className="font-semibold text-gray-900 dark:text-gray-100">{candidate.firstName}</span>
          </div>

          {/* Original Job Title */}
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Original Title:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{candidate.originalTitle}</span>
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Duration:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatDateRange(candidate.dates)}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Action Button */}
      <div className="pt-2">
        <Link to={`/candidates/${candidate.id}`}>
          <Button variant="outline" size="sm" className="w-full">
            <ExternalLink className="h-3 w-3 mr-2" />
            {t('companies.positions.viewCandidate')}
          </Button>
        </Link>
      </div>
    </div>
  );
};

const PositionCard = ({ position, onSeeAll }: { position: CompanyPosition; onSeeAll: (position: CompanyPosition) => void }) => {
  const { t } = useTranslation();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Card className="border-2 border-blue-200 hover:border-blue-300 transition-colors duration-200 cursor-pointer mb-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{position.title}</h4>
                  <Info className="h-3.5 w-3.5 text-gray-400" />
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <Users className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {position.candidateCount} {(position.candidateCount === 1 ? 'candidate' : 'candidates')}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <div className="text-2xl font-bold text-blue-500">
                  {position.candidateCount}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 max-h-[60vh] overflow-hidden" 
        side="bottom" 
        align="start"
        sideOffset={5}
        avoidCollisions={true}
        collisionPadding={20}
        sticky="always"
      >
        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
          <div className="flex items-center justify-between sticky top-0 bg-white dark:bg-gray-900 pb-2 border-b dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-blue-500" />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">{position.title} Role Breakdown</h4>
            </div>
            <div className="bg-blue-50 dark:bg-blue-950 text-blue-500 text-xs font-medium px-2.5 py-0.5 rounded-sm">
              {position.candidateCount}
            </div>
          </div>

          {position.candidates.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Based on candidates who worked in this role:
              </p>
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {/* Showing top 5 of {position.candidates.length} candidates */}
                {(() => {
                  return position.candidates.slice(0, 5);
                })().map((candidate, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-950 rounded-sm">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{candidate.firstName}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{formatDateRange(candidate.dates)}</p>
                    </div>
                    <div className="ml-2 flex items-center gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Info className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent 
                          className="w-64" 
                          side="left" 
                          align="start"
                          avoidCollisions={true}
                          collisionPadding={20}
                        >
                          <CandidatePopover 
                            candidate={candidate} 
                            positionTitle={position.title} 
                          />
                        </PopoverContent>
                      </Popover>
                      <Link to={`/candidates/${candidate.id}`}>
                        <Button variant="outline" size="sm">
                          <ExternalLink className="h-3 w-3 mr-1" />
                          View
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
                {position.candidates.length > 5 && (
                  <div className="text-center py-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      +{position.candidates.length - 5} more candidates
                    </p>
                  </div>
                )}
                <div className="text-center py-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => onSeeAll(position)}
                    className="w-full"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    See All ({position.candidateCount})
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              <Briefcase className="h-8 w-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
              <p className="text-sm">No candidates found for this role</p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const PositionHeatmapCard = ({ 
  position, 
  allPositions, 
  onSeeAll 
}: { 
  position: CompanyPosition; 
  allPositions: CompanyPosition[];
  onSeeAll: (position: CompanyPosition) => void;
}) => {
  const { t } = useTranslation();
  
  // Calculate intensity based on candidate count (0-100%)
  const maxCandidates = Math.max(...allPositions.map(p => p.candidateCount));
  const intensity = Math.min((position.candidateCount / maxCandidates) * 100, 100);
  
  // Solid color based on intensity
  const getIntensityColor = (intensity: number) => {
    if (intensity >= 80) return 'bg-blue-700';
    if (intensity >= 60) return 'bg-blue-500';
    if (intensity >= 40) return 'bg-sky-500';
    if (intensity >= 20) return 'bg-cyan-400';
    return 'bg-teal-300';
  };

  const getIntensityBg = (intensity: number) => {
    if (intensity >= 80) return 'bg-indigo-50 border-indigo-200 dark:bg-indigo-950 dark:border-indigo-800';
    if (intensity >= 60) return 'bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800';
    if (intensity >= 40) return 'bg-sky-50 border-sky-200 dark:bg-sky-950 dark:border-sky-800';
    if (intensity >= 20) return 'bg-cyan-50 border-cyan-200 dark:bg-cyan-950 dark:border-cyan-800';
    return 'bg-teal-50 border-teal-200 dark:bg-teal-950 dark:border-teal-800';
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Card className={`${getIntensityBg(intensity)} hover:shadow-lg transition-all duration-300 cursor-pointer group`}>
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Header with intensity indicator */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getIntensityColor(intensity)}`}></div>
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{position.title}</h4>
                </div>
                <Info className="h-4 w-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
              </div>

              {/* Candidate count with visual intensity */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Candidates</span>
                  <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{position.candidateCount}</span>
                </div>
                <SeniorityPills breakdown={position.seniorityBreakdown} size="sm" />
              </div>
              
              {/* Quick stats */}
              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  <span>{position.candidateCount} {(position.candidateCount === 1 ? 'candidate' : 'candidates')}</span>
                </div>
                {position.companies && position.companies.length > 0 && (
                  <div className="flex items-center gap-1 min-w-0">
                    <Building2 className="h-4 w-4 shrink-0" />
                    <span className="truncate">{position.companies.length} {position.companies.length === 1 ? 'company' : 'companies'}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-blue-500" />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">{position.title} Role Breakdown</h4>
            </div>
            <div className="bg-blue-50 dark:bg-blue-950 text-blue-500 text-xs font-medium px-2.5 py-0.5 rounded-sm">
              {position.candidateCount}
            </div>
          </div>

          {position.candidates.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Based on candidates who worked in this role:
              </p>
              {position.candidates.slice(0, 5).map((candidate, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-950 rounded-sm">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{candidate.firstName}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{formatDateRange(candidate.dates)}</p>
                  </div>
                  <div className="ml-2 flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <Info className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64">
                        <CandidatePopover 
                          candidate={candidate} 
                          positionTitle={position.title} 
                        />
                      </PopoverContent>
                    </Popover>
                    <Link to={`/candidates/${candidate.id}`}>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="h-3 w-3 mr-1" />
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
              {position.candidates.length > 5 && (
                <div className="text-center py-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    +{position.candidates.length - 5} more candidates
                  </p>
                </div>
              )}
              <div className="text-center py-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSeeAll(position)}
                  className="w-full"
                >
                  <Users className="h-4 w-4 mr-2" />
                  See All ({position.candidateCount})
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              <Briefcase className="h-8 w-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
              <p className="text-sm">No candidates found for this role</p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Modal component for viewing all candidates
const CandidatesModal = ({ 
  position, 
  isOpen, 
  onClose 
}: { 
  position: CompanyPosition | null; 
  isOpen: boolean; 
  onClose: () => void; 
}) => {
  const { t } = useTranslation();

  if (!position) return null;

  // Calculate insights
  const totalCandidates = position.candidateCount;
  const currentYear = new Date().getFullYear();
  const recentCandidates = position.candidates.filter(candidate => {
    const yearMatches = candidate.dates.match(/(\d{4})/g);
    if (yearMatches && yearMatches.length >= 2) {
      const endYear = parseInt(yearMatches[yearMatches.length - 1]);
      return endYear >= currentYear; // Current year or later
    } else if (yearMatches && yearMatches.length === 1) {
      const year = parseInt(yearMatches[0]);
      return year >= currentYear; // Current year or later
    }
    return false;
  }).length;

  const avgExperienceYears = position.candidates.reduce((acc, candidate) => {
    // Try to extract both start and end years from date range
    const yearMatches = candidate.dates.match(/(\d{4})/g);
    if (yearMatches && yearMatches.length >= 2) {
      const startYear = parseInt(yearMatches[0]);
      const endYear = parseInt(yearMatches[yearMatches.length - 1]);
      return acc + (endYear - startYear);
    } else if (yearMatches && yearMatches.length === 1) {
      // If only one year, assume it's recent experience
      const year = parseInt(yearMatches[0]);
      return acc + (currentYear - year);
    }
    return acc;
  }, 0) / totalCandidates;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl w-[95vw] max-h-[90vh] overflow-hidden p-0">
        {/* Header with gradient background */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 text-blue-900 dark:text-blue-100 p-6 border-b border-blue-200 dark:border-blue-800">
          <DialogHeader className="space-y-4">
            <div className="flex items-center justify-between pr-12">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-200 rounded-sm flex items-center justify-center">
                  <Briefcase className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-bold text-blue-900">
                    {position.title}
                  </DialogTitle>
                  <p className="text-blue-500 text-sm">
                    Complete candidate overview and insights
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-900">{totalCandidates}</div>
                <div className="text-blue-500 text-sm">Total Candidates</div>
              </div>
            </div>
            
            {/* Insights Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-white/60 dark:bg-gray-800/60 rounded-sm p-3 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-blue-800">Recent Activity</span>
                </div>
                <div className="text-xl font-bold mt-1 text-blue-900">{recentCandidates}</div>
                <div className="text-xs text-blue-500">Current year</div>
              </div>
              <div className="bg-white/60 dark:bg-gray-800/60 rounded-sm p-3 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm font-medium text-blue-800">Avg Experience</span>
                </div>
                <div className="text-xl font-bold mt-1 text-blue-900">{avgExperienceYears.toFixed(1)}</div>
                <div className="text-xs text-blue-500">Years in role</div>
              </div>
              <div className="bg-white/60 dark:bg-gray-800/60 rounded-sm p-3 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm font-medium text-blue-800">Role Diversity</span>
                </div>
                <div className="text-xl font-bold mt-1 text-blue-900">
                  {new Set(position.candidates.map(c => c.originalTitle)).size}
                </div>
                <div className="text-xs text-blue-500">Unique titles</div>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Content Area */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <div className="space-y-4">
            {/* Search and Filter Bar */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 mb-6">
              <div className="flex-1 relative w-full lg:w-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search candidates..." 
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center gap-2 w-full lg:w-auto">
                <Button variant="outline" size="sm" className="flex-1 lg:flex-none">
                  <Calendar className="h-4 w-4 mr-2" />
                  Sort by Date
                </Button>
                <Button variant="outline" size="sm" className="flex-1 lg:flex-none">
                  <User className="h-4 w-4 mr-2" />
                  Sort by Name
                </Button>
              </div>
            </div>

            {/* Candidates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {position.candidates.map((candidate, idx) => {
                const yearMatches = candidate.dates.match(/(\d{4})/g);
                let experienceYears = 0;
                let isRecent = false;
                
                if (yearMatches && yearMatches.length >= 2) {
                  const startYear = parseInt(yearMatches[0]);
                  const endYear = parseInt(yearMatches[yearMatches.length - 1]);
                  experienceYears = endYear - startYear;
                  isRecent = endYear >= currentYear;
                } else if (yearMatches && yearMatches.length === 1) {
                  const year = parseInt(yearMatches[0]);
                  experienceYears = currentYear - year;
                  isRecent = year >= currentYear;
                }
                
                return (
                  <div key={idx} className="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-sm p-5 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-500 transition-all duration-200">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-sm flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                          <User className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                            {candidate.firstName}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="px-2 py-1 rounded-sm text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                              {isRecent ? 'Current' : 'Experienced'}
                            </span>
                            {experienceYears > 0 && (
                              <span className="px-2 py-1 bg-blue-50 text-blue-500 rounded-sm text-xs font-medium">
                                {experienceYears}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Link to={`/candidates/${candidate.id}`}>
                        <Button variant="outline" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View
                        </Button>
                      </Link>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Briefcase className="h-4 w-4" />
                        <span className="truncate">{candidate.originalTitle}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDateRange(candidate.dates)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CompanyPositionsTab = ({
  companyId,
  companyName,
  positions,
  loading,
  error,
  searchTerm,
  setSearchTerm,
  viewMode,
  setViewMode
}: CompanyPositionsTabProps) => {
  const { t } = useTranslation();
  const [selectedPosition, setSelectedPosition] = useState<CompanyPosition | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-sm shadow p-6">
        <div className="text-center text-red-600">
          <p>{t('common.error')}: {error}</p>
        </div>
      </div>
    );
  }

  // Calculate stats
  const totalPositions = positions.length;
  const totalCandidates = positions.reduce((sum, pos) => sum + pos.candidateCount, 0);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-sm shadow">
      <div className="p-6">
        {/* Header with stats */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            {t('companies.detail.tabs.positions')}
          </h3>
          <div className="flex space-x-2">
            <input
              type="text"
              placeholder={t('companies.positions.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border rounded-sm text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
            <ViewToggle 
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              label=""
              allowedModes={['list', 'card']}
            />
          </div>
        </div>

        {/* Header Stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-3">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-blue-500 rounded-sm">
                  <Briefcase className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-blue-500 font-medium">Positions</p>
                  <p className="text-lg font-bold text-blue-500">{totalPositions}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-3">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-green-500 rounded-sm">
                  <TrendingUp className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-green-600 font-medium">Candidates</p>
                  <p className="text-lg font-bold text-green-700">{totalCandidates}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-sm"></div>
            ))}
          </div>
        ) : positions.length > 0 ? (
          <div className="space-y-4">
            {viewMode === 'list' ? (
              <Accordion type="multiple" className="w-full">
                {positions.map((position) => (
                  <AccordionItem key={position.title} value={position.title}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-4 gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <Briefcase className="h-4 w-4 text-blue-500 shrink-0" />
                          <span className="font-medium truncate">{position.title}</span>
                          {position.companies && position.companies.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                              <Building2 className="h-3 w-3 shrink-0" />
                              <span className="truncate">{position.companies.slice(0, 3).join(', ')}{position.companies.length > 3 ? ` +${position.companies.length - 3}` : ''}</span>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 shrink-0">
                          <SeniorityPills breakdown={position.seniorityBreakdown} size="sm" />
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            <span>{t('companies.positions.candidatesCount', { count: position.candidateCount })}</span>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 pt-2">
                        {position.candidates.slice(0, 5).map((candidate) => (
                          <div key={candidate.id} className="border dark:border-gray-700 rounded-sm p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                  <span className="font-medium">
                                    {candidate.firstName}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <Calendar className="h-3 w-3 text-gray-400" />
                                  <span className="text-sm text-gray-600 dark:text-gray-400">{formatDateRange(candidate.dates)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <Info className="h-4 w-4" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-64">
                                    <CandidatePopover 
                                      candidate={candidate} 
                                      positionTitle={position.title} 
                                    />
                                  </PopoverContent>
                                </Popover>
                                <Link to={`/candidates/${candidate.id}`}>
                                  <Button variant="outline" size="sm">
                                    {t('companies.positions.viewCandidate')}
                                  </Button>
                                </Link>
                              </div>
                            </div>
                          </div>
                        ))}
                        {position.candidates.length > 5 && (
                          <div className="text-center py-3 border-t mt-3">
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                              +{position.candidates.length - 5} more candidates
                            </p>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => {
                                setSelectedPosition(position);
                                setIsModalOpen(true);
                              }}
                              className="w-full"
                            >
                              <Users className="h-4 w-4 mr-2" />
                              See All ({position.candidateCount})
                            </Button>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="space-y-6">
                {/* Heatmap Legend */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-sm p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Position Intensity</h4>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Based on candidate count</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-teal-300"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">Low (1-20%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-cyan-400"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">Medium (20-40%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-sky-500"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">High (40-60%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">Very High (60-80%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-700"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">Highest (80%+)</span>
                    </div>
                  </div>
                </div>
                
                {/* Heatmap Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {positions.map((position) => (
                    <PositionHeatmapCard 
                      key={position.title} 
                      position={position} 
                      allPositions={positions}
                      onSeeAll={(position) => {
                        setSelectedPosition(position);
                        setIsModalOpen(true);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 border dark:border-gray-700 rounded-sm">
            <Briefcase className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto" />
            <p className="mt-2 text-gray-500 dark:text-gray-400">{t('companies.positions.noPositions')}</p>
          </div>
        )}
      </div>
      
      {/* Candidates Modal */}
      <CandidatesModal 
        position={selectedPosition}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPosition(null);
        }}
      />
    </div>
  );
};
